"use client";

import { useState, useSyncExternalStore } from "react";
import { AnatomySvg } from "@/components/anatomy/anatomy-svg";
import { SegmentedPicker } from "@/components/segmented-picker";
import { XIcon } from "@/components/icons";
import { BODY_REGIONS, type BodyRegion } from "@/lib/volume-by-muscle";

/**
 * Landing showcase muscle map — a self-contained, interactive demo of the
 * dashboard's MuscleMapCard: a count caption, the real `AnatomySvg` (front +
 * back), a VOLUME/PLAN SegmentedPicker, a decode legend, and a tap-a-muscle
 * drill-down (the tapped region glows the anatomy red + a panel names it).
 * Driven by static demo data — no real user data / Pro gating / view-as, so it
 * works standalone on the logged-out landing.
 *
 * ## Why the AnatomySvg mount is desktop-gated
 *
 * `AnatomySvg` fetches a ~580KB SVG per side ON MOUNT (useEffect), ignoring CSS
 * visibility. This showcase is rendered inside `hidden md:block`, so on a phone
 * it is still in the DOM — mounting `AnatomySvg` there would download ~1.1MB on
 * devices that never see it. So the two bodies only mount at >=768px; below
 * that we render empty aspect-matched placeholders (the surrounding caption /
 * toggle / legend always render, so there's no layout pop when the bodies
 * appear). `useSyncExternalStore` reads the media query SSR-safely (server
 * snapshot = false → placeholders on SSR + first hydration paint, no mismatch).
 *
 * Two instances render on the page (hero + the muscle-map feature row); the
 * module-level svgCache in anatomy-svg.tsx fetches each side once and reuses it.
 * Distinct `alignKey` per instance keeps their viewBox-alignment caches apart.
 */

// VOLUME = how much each region has been trained (heat intensity). A
// representative push/pull/legs spread — same values the dashboard tour uses.
const VOLUME_INTENSITY: Partial<Record<BodyRegion, number>> = {
  chest: 100,
  quads: 100,
  lats: 70,
  biceps: 70,
  front_delts: 45,
  rear_delts: 30,
  glutes: 45,
};
// PLAN = which of the 17 regions a routine set COVERS (binary). 10 of 17.
const PLAN_COVERED = new Set<BodyRegion>([
  "chest",
  "front_delts",
  "rear_delts",
  "lats",
  "biceps",
  "triceps",
  "quads",
  "hamstrings",
  "glutes",
  "calves",
]);
// Theme-aware untrained baseline (plain :root custom prop, not a Tailwind
// token — always emitted, re-themes with the app).
const UNTRAINED = "var(--color-muscle-untrained)";
// The region under review glows the anatomy asset's own native red — the
// convention from the dashboard drill-down ("the part you're inspecting").
const SELECTED_FILL = "#e82e30";

const TOTAL_REGIONS = BODY_REGIONS.length;
const TRAINED_COUNT = Object.keys(VOLUME_INTENSITY).length;
const COVERED_COUNT = PLAN_COVERED.size;

/** "upper_back" → "Upper Back" for the drill-down header. */
function regionLabel(region: string): string {
  return region.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// matchMedia as an external store — SSR-safe + lint-clean (no setState-in-effect).
function subscribeDesktop(onChange: () => void): () => void {
  const mq = window.matchMedia("(min-width: 768px)");
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
function getDesktopSnapshot(): boolean {
  return window.matchMedia("(min-width: 768px)").matches;
}
function getServerSnapshot(): boolean {
  return false;
}

export function ShowcaseMuscleMap({
  alignKey = "showcase-hero-map",
}: {
  alignKey?: string;
}) {
  const isDesktop = useSyncExternalStore(
    subscribeDesktop,
    getDesktopSnapshot,
    getServerSnapshot,
  );
  const [mode, setMode] = useState<"volume" | "plan">("volume");
  const [selectedRegion, setSelectedRegion] = useState<BodyRegion | null>(null);

  function fillFor(region: BodyRegion | null): string {
    if (region == null) return UNTRAINED; // outline + non-taxonomy muscles
    if (region === selectedRegion) return SELECTED_FILL; // drill-down glow
    if (mode === "plan") {
      return PLAN_COVERED.has(region) ? "var(--color-brand)" : UNTRAINED;
    }
    const pct = VOLUME_INTENSITY[region] ?? 0;
    if (pct <= 0) return UNTRAINED;
    return `color-mix(in oklab, var(--color-brand) ${pct}%, ${UNTRAINED})`;
  }

  function regionStatus(region: BodyRegion): string {
    if (mode === "plan") {
      return PLAN_COVERED.has(region) ? "In your plan" : "Not in your plan";
    }
    const pct = VOLUME_INTENSITY[region] ?? 0;
    if (pct >= 80) return "High volume";
    if (pct >= 40) return "Moderate volume";
    if (pct > 0) return "Light volume";
    return "Untrained";
  }

  const caption =
    mode === "volume"
      ? `${TRAINED_COUNT} of ${TOTAL_REGIONS} regions trained`
      : `${COVERED_COUNT} of ${TOTAL_REGIONS} regions covered`;

  return (
    <div className="flex flex-col gap-3">
      {/* Count caption — quick read alongside the bodies. */}
      <p className="text-center text-fg-muted text-xs uppercase tracking-wider font-medium">
        {caption}
      </p>

      {/* Bodies — gated to desktop (placeholders below md so phones never
          fetch the SVG). Same aspect per cell either way → no layout pop. */}
      <div className="grid grid-cols-2 gap-2 [&_svg]:aspect-[100/127]">
        {isDesktop ? (
          <>
            <AnatomySvg
              side="front"
              fillFor={fillFor}
              alignKey={alignKey}
              onRegionClick={(r) => setSelectedRegion(r)}
              selectedRegion={selectedRegion}
            />
            <AnatomySvg
              side="back"
              fillFor={fillFor}
              alignKey={alignKey}
              onRegionClick={(r) => setSelectedRegion(r)}
              selectedRegion={selectedRegion}
            />
          </>
        ) : (
          <>
            <div className="aspect-[100/127]" />
            <div className="aspect-[100/127]" />
          </>
        )}
      </div>

      {/* Drill-down — names the tapped region + its status; the X clears it. */}
      {selectedRegion && (
        <div className="rounded-md border border-border bg-surface px-3 py-2 flex items-center justify-between gap-2">
          <p className="text-fg text-sm font-semibold min-w-0 truncate">
            {regionLabel(selectedRegion)}
            <span className="text-fg-dim font-normal ml-2">
              {regionStatus(selectedRegion)}
            </span>
          </p>
          <button
            type="button"
            onClick={() => setSelectedRegion(null)}
            aria-label="Close region info"
            className="shrink-0 h-6 w-6 rounded-md text-fg-dim hover:text-fg hover:bg-surface-2 flex items-center justify-center transition-colors"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Decode legend — swaps with the mode. */}
      {mode === "volume" ? (
        <div className="flex items-center justify-center gap-2 text-fg-dim text-[10px] uppercase tracking-wider font-medium">
          <span>Less</span>
          <span
            aria-hidden
            className="h-1.5 w-20 rounded-full"
            style={{
              background: `linear-gradient(to right, ${UNTRAINED}, color-mix(in oklab, var(--color-brand) 50%, ${UNTRAINED}), var(--color-brand))`,
            }}
          />
          <span>More</span>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-3 text-fg-dim text-[10px] uppercase tracking-wider font-medium">
          <span className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="h-1.5 w-3 rounded-sm"
              style={{ background: "var(--color-brand)" }}
            />
            Covered
          </span>
          <span className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="h-1.5 w-3 rounded-sm"
              style={{ background: UNTRAINED }}
            />
            Gap
          </span>
        </div>
      )}

      {/* VOLUME / PLAN toggle — the app's shared SegmentedPicker. */}
      <div className="flex justify-center">
        <SegmentedPicker
          ariaLabel="Muscle map mode"
          options={
            [
              { value: "volume", label: "Volume" },
              { value: "plan", label: "Plan" },
            ] as const
          }
          value={mode}
          onSelect={(next) => setMode(next)}
          pending={false}
          size="compact"
          buttonExtras="uppercase tracking-wider"
        />
      </div>
    </div>
  );
}
